import React from 'react';
import packageJson from '../../../package.json';
import { cn } from '../ui/cn';
import { ChevronLeft, ChevronRight, LogOut } from '../../icons';
import BrandMark from './BrandMark';
import { getNavItems } from './navItems';

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  return (parts.length === 1 ? parts[0].charAt(0) : parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Desktop persistent sidebar (≥ lg). Expanded 240px with labels, collapsed 72px icon rail.
 * The collapsed state is owned by the parent (persisted via useSidebarCollapsed).
 */
const Sidebar = ({
  currentPage,
  onNavigate,
  opportunityCount = 0,
  isAdmin = false,
  collapsed = false,
  onToggleCollapsed,
  userDisplayName,
  userEmail,
  onSignOut,
}) => {
  const items = getNavItems(isAdmin);

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={cn(
        'hidden lg:flex fixed top-0 left-0 h-full z-40 flex-col bg-sidebar border-r border-[#1B3F4D] text-primary transition-[width] duration-200 ease-out',
        collapsed ? 'w-rail' : 'w-sidebar'
      )}
    >
      {/* Brand */}
      <div className={cn('flex items-center gap-3 h-[72px] px-4 flex-shrink-0', collapsed && 'justify-center px-0')}>
        <BrandMark size={36} />
        {!collapsed && <span className="text-[17px] font-semibold tracking-tight">BZ-Tracker</span>}
      </div>

      {/* Navigation */}
      <nav className={cn('flex-1 px-3 space-y-1 overflow-y-auto', collapsed && 'px-3')} aria-label="Primary">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;
          const badge = item.badgeKey === 'opportunities' ? opportunityCount : 0;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => onNavigate(item.id)}
              title={collapsed ? item.label : undefined}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative w-full flex items-center gap-3 rounded-xl text-left text-[15px] font-medium transition-colors duration-150',
                collapsed ? 'justify-center h-12' : 'h-11 px-3.5',
                isActive
                  ? 'bg-accent-soft text-accent'
                  : 'text-[#B7CBD2] hover:bg-white/5 hover:text-primary'
              )}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
              {badge > 0 && (
                <span
                  className={cn(
                    'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-danger text-white text-[11px] font-semibold tabular',
                    collapsed && 'absolute top-1 right-1 min-w-[18px] h-[18px] text-[10px]'
                  )}
                >
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User + collapse */}
      <div className={cn('flex-shrink-0 border-t border-[#1B3F4D] p-3', collapsed && 'px-2')}>
        <div className={cn('flex items-center gap-3', collapsed && 'flex-col gap-2')}>
          <div
            className="w-10 h-10 rounded-full bg-accent-soft text-accent flex items-center justify-center text-sm font-semibold flex-shrink-0"
            title={userEmail || userDisplayName}
          >
            {getInitials(userDisplayName)}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{userDisplayName || 'User'}</div>
              <div className="text-xs text-[#7FA0AB] truncate">v{packageJson.version}</div>
            </div>
          )}
          {!collapsed && onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              title="Sign out"
              aria-label="Sign out"
              className="w-9 h-9 flex items-center justify-center rounded-lg text-[#7FA0AB] hover:text-danger hover:bg-white/5"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className="w-9 h-9 flex items-center justify-center rounded-full border border-[#1B3F4D] bg-sidebar-2 text-accent hover:bg-accent-soft"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
